import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cache the config fetch across all button instances (same-origin, cheap).
let _cfgPromise = null;
const getGoogleConfig = () => {
  if (!_cfgPromise) {
    _cfgPromise = axios
      .get(`${API}/auth/google/config`)
      .then((r) => r.data)
      .catch(() => ({ enabled: false, client_id: '' }));
  }
  return _cfgPromise;
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startManaged = () => {
  const redirect = `${window.location.origin}/welcome`;
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
};

// Self-managed popup (auth-code) flow — keeps the app's own button styling.
const SelfManagedButton = ({ className, children, testid, onDone }) => {
  const { loginWithGoogleCode } = useAuth();
  const [busy, setBusy] = useState(false);
  const login = useGoogleLogin({
    flow: 'auth-code',
    onSuccess: async ({ code }) => {
      setBusy(true);
      const res = await loginWithGoogleCode(code);
      setBusy(false);
      if (res.success) onDone?.(res.user);
      else toast.error(res.error || 'Google pieslēgšanās neizdevās');
    },
    onError: () => toast.error('Google pieslēgšanās neizdevās'),
  });
  return (
    <button type="button" onClick={() => login()} disabled={busy} className={className} data-testid={testid}>
      {children}
    </button>
  );
};

/**
 * Drop-in "Continue with Google" button. Uses the app owner's own Google OAuth
 * (self-managed, works on custom domains) when configured, otherwise falls back
 * to Emergent-managed Google Auth. Preserves whatever styling/children the
 * caller passes so each page keeps its look.
 */
export const GoogleAuthButton = ({ className, children, onDone, testid = 'login-google-btn' }) => {
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    let on = true;
    getGoogleConfig().then((c) => { if (on) setCfg(c); });
    return () => { on = false; };
  }, []);

  // Until config resolves, render a disabled button so a fast click can't start
  // the managed flow on a custom domain where it would fail.
  if (cfg === null) {
    return (
      <button type="button" disabled className={className} style={{ opacity: 0.7 }} data-testid={testid}>
        {children}
      </button>
    );
  }

  if (cfg.enabled && cfg.client_id) {
    return (
      <GoogleOAuthProvider clientId={cfg.client_id}>
        <SelfManagedButton className={className} testid={testid} onDone={onDone}>
          {children}
        </SelfManagedButton>
      </GoogleOAuthProvider>
    );
  }

  return (
    <button type="button" onClick={startManaged} className={className} data-testid={testid}>
      {children}
    </button>
  );
};
